import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, filter, map, of, switchMap, tap } from 'rxjs';

import { AnalyticsTrackEvent, TrackEventsService } from 'app/shared/analytics';
import { KpiNotificationDialogComponent } from 'app/shared/dialogs/kpi-notifications-dialog/kpi-notifications-dialog.component';
import { ErrorMessage, HttpErrorHandlingService } from 'app/shared/error-handling';
import { ReportsConfiguration } from 'app/shared/report-model/configuration';
import { AppActions } from 'app/state';
import { AppFacade } from 'app/state/app.facade';

import { KpiNotificationActions } from '.';
import * as fromReportDetails from '../../report-details/state/reducers';
import { KpiNotificationSettings } from '../kpi-notification-settings.model';

@Injectable()
export class ReportOverviewEffects {
    openKpiNotificationSettingsDialog$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(KpiNotificationActions.updateKpiSettings),
            exhaustMap(({ reportId }) => {
                this.store$.dispatch(
                    AppActions.openDialog({
                        dialog: {
                            component: KpiNotificationDialogComponent,
                            inputs: { reportId: reportId },
                        },
                    }),
                );

                return of(KpiNotificationActions.loadKpiNotificationSettings({ reportId: reportId }));
            }),
        );
    });

    editKpiNotifications$ = createEffect(() =>
        this.actions$.pipe(
            ofType(KpiNotificationActions.updateKpiSettings),
            exhaustMap(({ reportId }) => {
                const url: string =
                    this.reportsConfiguration.apiPaths.getReportKpiNotificationSettings(reportId);

                return this.httpClient.get<KpiNotificationSettings>(url).pipe(
                    map((kpiNotificationSettings: KpiNotificationSettings) =>
                        KpiNotificationActions.loadKpiNotificationSettingsSuccess({
                            reportId,
                            kpiNotificationSettings,
                        }),
                    ),
                    catchError((error: HttpErrorResponse) => {
                        const errorMessage: ErrorMessage =
                            this.errorHandlingService.getErrorMessageObject(error);
                        return of(
                            KpiNotificationActions.loadKpiNotificationSettingsError({ error: errorMessage }),
                        );
                    }),
                );
            }),
        ),
    );

    saveKpiNotifications$ = createEffect(() =>
        this.actions$.pipe(
            ofType(KpiNotificationActions.saveKpiNotificationSettings),
            exhaustMap(({ reportId, kpiNotificationSettings }) => {
                const url: string =
                    this.reportsConfiguration.apiPaths.getReportKpiNotificationSettings(reportId);

                return this.httpClient.put<KpiNotificationSettings>(url, kpiNotificationSettings).pipe(
                    map(() =>
                        KpiNotificationActions.saveKpiNotificationSettingsSuccess({
                            reportId,
                            kpiNotificationSettings,
                        }),
                    ),
                    catchError((error: HttpErrorResponse) => {
                        const errorMessage: ErrorMessage =
                            this.errorHandlingService.getErrorMessageObject(error);
                        return of(
                            KpiNotificationActions.saveKpiNotificationSettingsError({ error: errorMessage }),
                        );
                    }),
                );
            }),
        ),
    );

    closeDialogOnSaveSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(KpiNotificationActions.saveKpiNotificationSettingsSuccess),
                tap(() => this.appFacade.closeDialog()),
            );
        },
        { dispatch: false },
    );

    processNotificationOnSaveSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(KpiNotificationActions.saveKpiNotificationSettingsSuccess),
            map((action) => {
                const isActivePresent = this.checkIfActivePresent(action.kpiNotificationSettings);
                const notificationKey = isActivePresent ? 'ENABLED' : 'DISABLED';
                return AppActions.showInfoNotification({
                    notification: {
                        message: `REPORT_DIALOG.EDIT_KPI_NOTIFICATIONS.${notificationKey}.INFO_TOASTER_MESSAGE`,
                        emphasized: `REPORT_DIALOG.EDIT_KPI_NOTIFICATIONS.${notificationKey}.INFO_TOASTER_TITLE`,
                    },
                });
            }),
        );
    });

    trackKpiNotificationEnabled$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(KpiNotificationActions.saveKpiNotificationSettingsSuccess),
                filter((action) => this.checkIfActivePresent(action.kpiNotificationSettings)),
                switchMap((action) => this.getReportWithAction(action)),
                tap(({ action, report }) => {
                    this.trackEventsService.track({
                        type: AnalyticsTrackEvent.kpiNotificationEnabled,
                        context: {
                            siteId: report.siteId,
                            siteName: report.location,
                            reportId: action.reportId,
                            editionId: report.reportEditionId,
                        },
                    });
                }),
            );
        },
        { dispatch: false },
    );

    private getReportWithAction(
        action: ReturnType<typeof KpiNotificationActions.saveKpiNotificationSettingsSuccess>,
    ) {
        return this.store$.select(fromReportDetails.selectReport).pipe(
            switchMap((report) =>
                this.store$
                    .select(fromReportDetails.selectReportEdition)
                    .pipe(map((edition) => ({ report, edition }))),
            ),
            map(({ report, edition }) => ({
                action,
                report: {
                    ...report,
                    reportEditionId: edition?.reportEditionId,
                },
            })),
        );
    }

    private checkIfActivePresent(
        kpiNotificationSettings: ReturnType<
            typeof KpiNotificationActions.saveKpiNotificationSettingsSuccess
        >['kpiNotificationSettings'],
    ) {
        const { kpiSetLimits, ungroupedLimits } = kpiNotificationSettings;
        return (
            kpiSetLimits.some((group) => group.limits.some((limit) => limit.isActive)) ||
            ungroupedLimits.some((limit) => limit.isActive)
        );
    }

    constructor(
        private readonly actions$: Actions,
        private readonly httpClient: HttpClient,
        private readonly store$: Store<fromReportDetails.State>,
        private readonly errorHandlingService: HttpErrorHandlingService,
        private readonly reportsConfiguration: ReportsConfiguration,
        private readonly appFacade: AppFacade,
        private readonly trackEventsService: TrackEventsService,
    ) {}
}
